import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { GeneralDataService } from '@app/services/general/general-data.service';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { OrquestadorService } from '@app/services/orquestador/orquestador.service';
import { selectDataByKey } from 'src/storage/general.selectors';
import { ensureNewFlowSessionContextOrRedirect } from '@app/utils/new-flow-session.util';

/**
 * Componente de loader que valida el score del inquilino
 * y redirige según el resultado
 */
@Component({
  selector: 'app-new-loader',
  templateUrl: './new-loader.component.html',
  styleUrls: ['./new-loader.component.scss'],
})
export class LoaderComponent implements OnInit, OnDestroy {
  private readonly REDIRECT_DELAY = 10000; // 10 segundos
  private readonly LOADER_ROUTE = '/seguros/hogar/arriendo/cotizador/loader';

  // Servicios inyectados
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly generalDataService = inject(GeneralDataService);
  private readonly orquestadorService = inject(OrquestadorService);
  private readonly store = inject(Store);

  // Propiedades del componente
  body: any;
  asesorCode = '';
  bitacoraId = '';

  steps = [
    { title: 'Revisando los datos de tu arriendo.', loading: true, imageUrl: './assets/img/Cargador.png' },
    { title: 'Revisando la capacidad de pago del inquilino.', loading: true, imageUrl: './assets/img/Cargador.png' },
    { title: 'Calculando el mejor plan para tu arriendo.', loading: true, imageUrl: './assets/img/Cargador.png' },
    { title: 'Seguridad y tranquilidad para tu arriendo', loading: true, imageUrl: './assets/img/Cargador.png' },
  ];

  private subscription = new Subscription();

  async ngOnInit(): Promise<void> {
    try {

      const ok = await ensureNewFlowSessionContextOrRedirect(this.store, this.router);
      if (!ok) return;

      // Suscribirse al código de asesor
      this.subscription.add(
        this.store.select(selectDataByKey('codigoAsesor')).subscribe(code => {
          if (code) {
            console.log('Código de asesor desde el store:', code);
            this.asesorCode = code;
          }
        })
      );

      this.loadScoreAndNavigate();
    } catch (error) {
      console.error('Error en ngOnInit del loader:', error);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Carga el score del inquilino y navega según el resultado
   */
  private loadScoreAndNavigate(): void {
    const queryParams = this.asesorCode ? { codigoAsesor: this.asesorCode } : {};

    const options = {
      keys: ['Tenant', 'Property'],
      screenPercentage: '10',
      leadId: 'leadId',
      getScore: true,
      asesorCode: this.asesorCode || undefined,
      bitacoraId: this.bitacoraId || undefined
    };

    console.log('📊 Obteniendo datos del store para loader...');

    this.generalDataService.getbody(options).subscribe(body => {
      this.body = body;
      console.log('Generated body (loader):', this.body);
      console.log('📋 leadId en body:', this.body.leadId ? `✅ ${this.body.leadId}` : '⚠️ No disponible');

      this.validateScoreIfNeeded(queryParams);
    });
  }

  /**
   * Valida el score solo si estamos en la ruta del loader
   */
  private validateScoreIfNeeded(queryParams: any): void {
    const currentPath = this.router.url.split('?')[0];
    console.log('🔍 Ruta actual del loader:', currentPath);

    if (!currentPath.startsWith(this.LOADER_ROUTE)) {
      console.warn('⚠️ Ruta no coincide con', this.LOADER_ROUTE);
      return;
    }

    console.log('✅ Ejecutando validación de score en loader...');

    this.orquestadorService.postOrquestador(this.body).subscribe(response => {
      if (!response) {
        console.warn('⚠️ No se recibió respuesta del orquestador');
        return;
      }

      this.startStepValidationSequence();
      this.handleScoreResponse(response, queryParams);
    });
  }

  /**
   * Maneja la respuesta del score y navega según el resultado
   */
  private handleScoreResponse(response: any, queryParams: any): void {
    console.log('📦 Respuesta del orquestador en loader:', response);

    const scoreAnalitico = response?.data?.scoreAnalitico;

    if (!scoreAnalitico) {
      console.warn('⚠️ No se recibió scoreAnalitico en la respuesta');
      return;
    }

    console.log('🎯 Score Analítico:', scoreAnalitico);

    switch (scoreAnalitico) {
      case 'Suscribe':
        this.handleApprovedScore(queryParams);
        break;

      case 'Falló el servicio':
        this.handleServiceError(queryParams);
        break;

      case 'No suscribe (sin posibilidad, incluso con Codeudor)':
        this.handleRejectedScore(queryParams);
        break;

      default:
        this.handleUnapprovedScore(queryParams);
        break;
    }
  }

  /**
   * Maneja el caso de score aprobado
   */
  private handleApprovedScore(queryParams: any): void {
    console.log('✅ Score aprobado - Navegando a planes con datos actualizados');
    localStorage.removeItem('state_service');
    this.router.navigate(['seguros/hogar/arriendo/cotizador/planes'], { queryParams });
  }

  /**
   * Maneja el caso de error en el servicio de score
   */
  private handleServiceError(queryParams: any): void {
    console.log('⚠️ Error en servicio de score - Redirigiendo a cotización-pausa');
    localStorage.setItem('state_service', 'fallo');
    setTimeout(() => {
      this.router.navigate(['seguros/hogar/arriendo/cotizador/cotizacion-pausa'], { queryParams });
    }, this.REDIRECT_DELAY);
  }

  /**
   * Maneja el caso de score rechazado sin posibilidad de codeudor
   */
  private handleRejectedScore(queryParams: any): void {
    console.log('❌ Score rechazado sin posibilidad de codeudor - Redirigiendo a no-cumple-politicas');
    setTimeout(() => {
      this.router.navigate(['seguros/hogar/arriendo/cotizador/no-cumple-politicas'], { queryParams });
    }, this.REDIRECT_DELAY);
  }

  /**
   * Maneja el caso de score no aprobado
   */
  private handleUnapprovedScore(queryParams: any): void {
    console.log('⚠️ Score no aprobado - Redirigiendo a cotización-pausa');
    this.generalDataService.llenadodatosgenerales().subscribe(() => {
      localStorage.removeItem('state_service');
      setTimeout(() => {
        this.router.navigate(['seguros/hogar/arriendo/cotizador/cotizacion-pausa'], { queryParams });
      }, this.REDIRECT_DELAY);
    });
  }

  /**
   * Completa un paso del loader visual
   */
  private completeStep(index: number): void {
    this.steps[index].loading = false;
    this.steps[index].imageUrl = './assets/img/check32.png';
  }

  /**
   * Inicia la secuencia de validación de pasos (animación visual)
   */
  private startStepValidationSequence(): void {
    [1000, 2000, 3000, 4000].forEach((delay, index) => {
      setTimeout(() => {
        this.completeStep(index);
        this.cdr.detectChanges();
      }, delay);
    });
  }
}
